/**
 * Chrome / Edge speech recognition via the Web Speech API (webkit prefix in Chromium).
 */

type BrowserSpeechRecognitionResult = {
  isFinal: boolean;
  0: { transcript: string };
};

type BrowserSpeechRecognitionResultEvent = {
  resultIndex: number;
  results: ArrayLike<BrowserSpeechRecognitionResult>;
};

type BrowserSpeechRecognitionErrorEvent = {
  error: string;
};

type BrowserSpeechRecognition = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: BrowserSpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: BrowserSpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
};

type SpeechRecognitionConstructor = new () => BrowserSpeechRecognition;

function resolveSpeechRecognitionConstructor(): SpeechRecognitionConstructor | null {
  if (typeof globalThis === "undefined") return null;
  const browserWindow = globalThis as typeof globalThis & {
    SpeechRecognition?: SpeechRecognitionConstructor;
    webkitSpeechRecognition?: SpeechRecognitionConstructor;
  };
  return browserWindow.SpeechRecognition ?? browserWindow.webkitSpeechRecognition ?? null;
}

export function chromeSpeechRecognitionSupported(): boolean {
  return resolveSpeechRecognitionConstructor() != null;
}

export function createChromeSpeechRecognition(): BrowserSpeechRecognition | null {
  const ctor = resolveSpeechRecognitionConstructor();
  if (ctor == null) return null;
  try {
    const recognition = new ctor();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-US";
    return recognition;
  } catch {
    return null;
  }
}

export function appendSpeechTranscript(current: string, transcript: string): string {
  const next = transcript.trim();
  if (next === "") return current;
  const base = current.trim();
  return base === "" ? next : `${base} ${next}`;
}
